import type { ApiPropertyProject } from '@kaiizen/kai-pro-api';

import { getDateValue } from './dashboardHelpers';
import type { DashboardView } from './dashboardTypes';

export type TurnBasedDashboardArtifact = 'scope' | 'changeOrder' | 'punchList' | 'closeOut';
export type TurnBasedDashboardStatus = 'inProgress' | 'approved';
export type TurnBasedDashboardWorkView = 'myWork' | 'myTeam' | 'allTeams' | 'allJobs';

export type TurnBasedDashboardArtifactPresentation = {
  artifact: TurnBasedDashboardArtifact;
  label: string;
  status: TurnBasedDashboardStatus;
};

/** Returns the scan whose current workflow state is represented in the dashboard. */
export const getTurnBasedDashboardScan = (project: ApiPropertyProject) => project.latestScan ?? project.latestApprovedScan ?? project.firstScan;

/**
 * Completion is a project-level state: close-out approval completes the project,
 * and reactivation returns it to active. Do not use firstApprovedCloseoutScan,
 * which intentionally remains populated after a project is reactivated.
 */
export const isTurnBasedDashboardProjectCompleted = (project: ApiPropertyProject) => project.status === 'completed';

/** Maps persisted scan types to the artifact labels already established in Renovator. */
export const getTurnBasedDashboardArtifactPresentation = (
  project: ApiPropertyProject,
): TurnBasedDashboardArtifactPresentation => {
  if (isTurnBasedDashboardProjectCompleted(project)) {
    return { artifact: 'closeOut', label: 'Close Out', status: 'approved' };
  }

  const scan = getTurnBasedDashboardScan(project);
  if (scan?.scanType === 'changeRequest') {
    return {
      artifact: 'changeOrder',
      label: 'Change Order',
      status: scan.scanLifecycleStatus === 'approved' ? 'approved' : 'inProgress',
    };
  }
  if (scan?.scanType === 'progressRework') {
    return {
      artifact: 'punchList',
      label: 'Punch List',
      status: scan.scanLifecycleStatus === 'approved' ? 'approved' : 'inProgress',
    };
  }
  if (scan?.scanType === 'progressCompleted') {
    return {
      artifact: 'closeOut',
      label: 'Close Out',
      status: scan.scanLifecycleStatus === 'approved' ? 'approved' : 'inProgress',
    };
  }

  return {
    artifact: 'scope',
    label: 'Scope',
    status: scan?.scanType === 'progress' || scan?.scanLifecycleStatus === 'approved'
      ? 'approved'
      : 'inProgress',
  };
};

/** Keeps active projects out of the completed view even if they have a historical close-out. */
export const projectMatchesTurnBasedDashboardView = (
  project: ApiPropertyProject,
  dashboardView: 'active' | 'closed',
) =>
  dashboardView === 'closed'
    ? isTurnBasedDashboardProjectCompleted(project)
    : !isTurnBasedDashboardProjectCompleted(project);

/** Matches a lifecycle tab while keeping archived projects exclusive to the Archived view. */
export const projectMatchesTurnBasedDashboardListView = (
  project: ApiPropertyProject,
  dashboardView: DashboardView,
) => {
  if (dashboardView === 'archived') return !!project.archivedAt;

  return !project.archivedAt && projectMatchesTurnBasedDashboardView(project, dashboardView);
};

/**
 * Matches the signed-in user's current responsibility, manager queue, or
 * another holder's turn within the field user's server-authorized projects.
 */
export const projectMatchesTurnBasedDashboardWorkView = (
  project: ApiPropertyProject,
  userId: string | undefined,
  workView: TurnBasedDashboardWorkView,
  options?: { includeOtherAssignedProjectHolders?: boolean },
) => {
  if (workView === 'allJobs' || workView === 'allTeams') return true;
  if (!userId) return false;
  if (workView === 'myWork') return project.responsibleUserId === userId;

  return project.responsibleUserId !== userId
    && (options?.includeOtherAssignedProjectHolders || (project.managerIds ?? []).includes(userId));
};

/** Counts lifecycle tabs after applying the selected work-view responsibility scope. */
export const getTurnBasedDashboardViewCounts = ({
  projects,
  userId,
  workView,
  includeOtherAssignedProjectHolders,
}: {
  projects: readonly ApiPropertyProject[];
  userId: string | undefined;
  workView: TurnBasedDashboardWorkView;
  includeOtherAssignedProjectHolders?: boolean;
}) => {
  const workScopedProjects = projects.filter(project =>
    projectMatchesTurnBasedDashboardWorkView(project, userId, workView, { includeOtherAssignedProjectHolders })
  );

  return {
    active: workScopedProjects.filter(project => projectMatchesTurnBasedDashboardListView(project, 'active')).length,
    closed: workScopedProjects.filter(project => projectMatchesTurnBasedDashboardListView(project, 'closed')).length,
    archived: workScopedProjects.filter(project => projectMatchesTurnBasedDashboardListView(project, 'archived')).length,
  };
};

/** Returns the date the dashboard displays as its delivery due date. */
export const getTurnBasedDashboardDueDate = (project: ApiPropertyProject) =>
  getTurnBasedDashboardScan(project)?.customerMetadata?.dueDate
    ?? project.firstScan?.customerMetadata?.dueDate;

/** Identifies only overdue active projects; review attention is intentionally excluded. */
export const isTurnBasedDashboardProjectPastDue = (project: ApiPropertyProject) => {
  if (isTurnBasedDashboardProjectCompleted(project)) return false;

  const dueDate = getDateValue(getTurnBasedDashboardDueDate(project));
  if (!dueDate) return false;

  const today = new Date();
  const dueUtc = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return dueUtc < todayUtc;
};

/** Splits a standard address into the street line and the smaller locality line. */
export const getTurnBasedDashboardAddress = (address: string | undefined) => {
  const parts = address?.split(',').map(part => part.trim()).filter(Boolean) ?? [];
  return {
    streetAddress: parts[0] ?? 'Address not available',
    locality: parts.slice(1).join(', '),
  };
};

/** Renders a compact, unambiguous owner label for a project whose turn belongs to someone else. */
export const getTurnBasedDashboardPersonName = (name: string | undefined) => {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length < 2) return parts[0];
  return `${parts[0]![0]}. ${parts.at(-1)}`;
};
